import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import api, { authAPI } from '../utils/api';
import { showGoodbyeToast } from '../utils/authToasts';

/**
 * Global Application Context
 * Manages authentication state, user data, and global app state
 * Following the documentation guidelines for state management
 */

// Action types
const ACTIONS = {
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  SET_USER: 'SET_USER',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  UPDATE_USER: 'UPDATE_USER',
};

// Initial state
const initialState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  token: localStorage.getItem('jwt_token') || null,
  roles: [],
  permissions: [],
};

// Reducer function
const appReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.LOGIN:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        token: action.payload.token,
        roles: action.payload.roles || [],
        permissions: action.payload.permissions || [],
        loading: false,
        error: null,
      };

    case ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        token: null,
        roles: [],
        permissions: [],
        loading: false,
        error: null,
      };

    case ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
      };

    case ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };

    case ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };

    default:
      return state;
  }
};

// Create context
const AppContext = createContext();

// Provider component
export const AppProvider = ({ children }) => {
  const [initialized, setInitialized] = React.useState(false);
  const [state, dispatch] = useReducer(appReducer, initialState);
  const navigate = useNavigate();

  // Helper to check token expiry
  const isTokenExpired = token => {
    try {
      const decoded = jwtDecode(token);
      if (!decoded.exp) return true;
      const now = Date.now() / 1000;
      return decoded.exp < now;
    } catch (e) {
      return true;
    }
  };

  // Check authentication status on mount (validate token with backend)
  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem('jwt_token');
      if (!token) return;

      // Expired → force logout
      if (isTokenExpired(token)) {
        localStorage.removeItem('jwt_token');
        dispatch({ type: ACTIONS.LOGOUT });
        navigate('/login');
        return;
      }

      try {
        dispatch({ type: ACTIONS.SET_LOADING, payload: true });
        // Choose profile endpoint based on token roles
        let profileUrl = '/api/auth/profile';
        try {
          const dec = jwtDecode(token);
          if(dec?.roles?.includes('ROLE_PUBLISHER')) profileUrl = '/api/publisher/me/profile';
        } catch(e){ /* ignore decode errors */ }
        const res = await api.get(profileUrl);
        dispatch({
          type: ACTIONS.LOGIN,
          payload: {
            user: res.data, // set full user object
            token,
            roles: res.data.roles || [],
            permissions: res.data.permissions || [],
          },
        });
      } catch (e) {
        // Token invalid on server → logout
        localStorage.removeItem('jwt_token');
        dispatch({ type: ACTIONS.LOGOUT });
        navigate('/login');
      }
    };

    verifyToken();
    setInitialized(true);
  }, [navigate]);

  // Logout function (defined early so hooks can depend on it)
  const logout = useCallback(async () => {
    const username = state.user?.username || state.user?.firstName || 'User';
    try {
      const token = localStorage.getItem('jwt_token');
      if (token) {
        await authAPI.logout();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Logout API error:', error);
    } finally {
      localStorage.removeItem('jwt_token');
      dispatch({ type: ACTIONS.LOGOUT });
      await showGoodbyeToast(username);
      navigate('/login');
    }
  }, [dispatch, navigate, state.user]);

  // Poll user status every 1 minute and auto-logout if inactive
  useEffect(() => {
    let interval;
    if (state.isAuthenticated) {
      interval = setInterval(async () => {
        try {
          const response = await api.get('/api/auth/profile');
          if (response.data.status && response.data.status.toLowerCase() !== 'active') {
            alert('Your account has been deactivated. Please contact admin.');
            logout();
          }
        } catch (e) {
          // Optionally handle error
        }
      }, 60000); // 1 minute
    }
    return () => clearInterval(interval);
  }, [state.isAuthenticated, logout]);

  // On login, decode JWT and/or use response to set roles/permissions
  const login = async () => {
    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: ACTIONS.CLEAR_ERROR });
      const token = localStorage.getItem('jwt_token');
      let roles = [];
      let permissions = [];
      let userObj = {};
      if (token) {
        const decoded = jwtDecode(token);
        roles = decoded.roles || [];
        permissions = decoded.permissions || [];
        const profileRes = await api.get('/api/auth/profile').catch(()=>null);
        if(profileRes?.data){
          userObj = profileRes.data;
          roles = profileRes.data.roles || roles;
          permissions = profileRes.data.permissions || permissions;
        } else {
          userObj = { username: decoded.sub || decoded.username || '' };
        }
      }
      dispatch({
        type: ACTIONS.LOGIN,
        payload: {
          user: userObj,
          token,
          roles,
          permissions,
        },
      });
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Login failed';
      dispatch({ type: ACTIONS.SET_ERROR, payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  /* Legacy logout kept for reference - replaced by useCallback earlier
  const legacyLogout = async () => {
    try {
      const token = localStorage.getItem('jwt_token');
      if (token) {
        await authAPI.logout(); // Call backend logout
      }
    } catch (error) {
      // Optionally log error or show notification
      // eslint-disable-next-line no-console
      console.error('Logout API error:', error);
    } finally {
      localStorage.removeItem('jwt_token');
      dispatch({ type: ACTIONS.LOGOUT });
      navigate('/login');
    }
  };
*/

  // Update user function
  const updateUser = userData => {
    dispatch({ type: ACTIONS.UPDATE_USER, payload: userData });
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: ACTIONS.CLEAR_ERROR });
  };

  // Provide roles and permissions in context value
  const value = {
    ...state,
    login,
    logout,
    updateUser,
    clearError,
    roles: state.roles,
    permissions: state.permissions,
  };

  if (!initialized) return null; // or a loader component

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

// Custom hook to use the context
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

// PropTypes
AppProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AppContext;
